import type { TFunction } from "i18next";

export interface ApiError {
  data?: {
    error?: string;
    message?: string;
    detail?: string;
    type?: string;
    details?: unknown;
    non_field_errors?: string[];
    [key: string]: unknown;
  };
}

const ISSUE_CODE_PATTERN = /^[A-Za-z0-9_]+$/;

const SNAKE_CASE_CODE_PATTERN = /^[a-z0-9]+(_[a-z0-9]+)+$/;

function translateErrorCode(message: string, t: TFunction): string {
  if (!SNAKE_CASE_CODE_PATTERN.test(message)) return message;
  return t(`errors.codes.${message}`, { ns: "Common", defaultValue: message });
}

function isEnvelope(data: unknown): data is { status: "error"; details?: unknown; message?: string } {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { status?: unknown }).status === "error"
  );
}

function findFirstFieldMessage(data: Record<string, unknown>): string | null {
  for (const [, value] of Object.entries(data)) {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "string") {
      return value[0];
    }
    if (typeof value === "string") {
      return value;
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nested = findFirstFieldMessage(value as Record<string, unknown>);
      if (nested !== null) return nested;
    }
  }
  return null;
}

export function extractErrorMessage(error: unknown, defaultKey: string, t: TFunction): string {
  const apiError = error as ApiError;

  if (apiError?.data && isEnvelope(apiError.data)) {
    if (Array.isArray(apiError.data.details) && apiError.data.details.length > 0) {
      const first = apiError.data.details[0] as { field?: string; issue?: string };
      if (first?.issue && typeof first.issue === "string") {
        if (!ISSUE_CODE_PATTERN.test(first.issue)) return first.issue;
        return t(`errors.codes.${first.issue}`, { ns: "Common", defaultValue: first.issue });
      }
    }
    if (typeof apiError.data.message === "string" && apiError.data.message.length > 0) {
      return apiError.data.message;
    }
    return t(defaultKey);
  }

  if (apiError?.data?.code === "revoke_forbidden") {
    return t("errors.codes.revoke_forbidden", { ns: "Common", defaultValue: apiError.data.error ?? t(defaultKey) });
  }

  if (
    apiError?.data?.non_field_errors &&
    Array.isArray(apiError.data.non_field_errors) &&
    apiError.data.non_field_errors.length > 0
  ) {
    return apiError.data.non_field_errors[0];
  }

  if (apiError?.data && typeof apiError.data === "object") {
    const firstMessage = findFirstFieldMessage(apiError.data);
    if (firstMessage !== null) {
      return translateErrorCode(firstMessage, t);
    }
  }

  if (apiError?.data?.error) {
    return translateErrorCode(apiError.data.error, t);
  }

  if (apiError?.data?.message) {
    return apiError.data.message;
  }

  if (apiError?.data?.detail) {
    return apiError.data.detail;
  }

  if (typeof error === "string") {
    return error;
  }

  return t(defaultKey);
}
